import { isMarkupType } from '../constants/nodeTypes';
import { pickMagneticTargetSocket } from './magneticConnect';
import { snapshotFromStore } from '../stores/undoStore';
import { useDiagramStore } from '../stores/diagramStore';
import { useUIStore } from '../stores/uiStore';
import type { Edge, Node } from '../types';

function defaultSourceSocket(node: Node) {
  return node.sockets.find(s => s.type === 'output') ?? node.sockets[0] ?? null;
}

export function connectTwoNodes(sourceId: string, targetId: string): Edge | undefined {
  if (sourceId === targetId) return undefined;
  const st = useDiagramStore.getState();
  const source = st.nodes.find(n => n.id === sourceId);
  const target = st.nodes.find(n => n.id === targetId);
  if (!source || !target) return undefined;
  if (isMarkupType(source.type) || isMarkupType(target.type)) return undefined;
  const sourceSocket = defaultSourceSocket(source);
  if (!sourceSocket) return undefined;
  const targetSocket = pickMagneticTargetSocket(source, sourceSocket, target)
    ?? target.sockets.find(s => s.type === 'input')
    ?? target.sockets[0];
  if (!targetSocket) return undefined;
  st.startConnection(sourceId, sourceSocket.id);
  return st.completeConnection(targetId, targetSocket.id);
}

export function connectNodeChain(nodeIds: string[]): number {
  const unique = nodeIds.filter((id, i, arr) => arr.indexOf(id) === i);
  if (unique.length < 2) return 0;
  snapshotFromStore();
  let created = 0;
  for (let i = 0; i < unique.length - 1; i++) {
    if (connectTwoNodes(unique[i], unique[i + 1])) created += 1;
  }
  return created;
}

function startFromNode(nodeId: string): boolean {
  const st = useDiagramStore.getState();
  const node = st.nodes.find(n => n.id === nodeId);
  if (!node || isMarkupType(node.type)) return false;
  const socket = defaultSourceSocket(node);
  if (!socket) return false;
  st.startConnection(nodeId, socket.id);
  return true;
}

/**
 * Click-to-connect while the E tool is armed.
 * First click starts from that node; second click completes the edge.
 * Sticky mode then continues from the target so you can chain.
 */
export function handleConnectToolNodeClick(nodeId: string): boolean {
  const ui = useUIStore.getState();
  if (ui.canvasConnectMode === 'none') return false;
  const st = useDiagramStore.getState();
  const node = st.nodes.find(n => n.id === nodeId);
  if (!node || isMarkupType(node.type)) return false;

  if (!st.connectionState.isConnecting) {
    startFromNode(nodeId);
    return true;
  }

  if (st.connectionState.sourceNodeId === nodeId) return true;

  snapshotFromStore();
  const source = st.nodes.find(n => n.id === st.connectionState.sourceNodeId);
  const sourceSocket = source?.sockets.find(s => s.id === st.connectionState.sourceSocketId);
  if (!source || !sourceSocket) {
    st.cancelConnection();
    return true;
  }
  const targetSocket = pickMagneticTargetSocket(source, sourceSocket, node)
    ?? node.sockets.find(s => s.type === 'input')
    ?? node.sockets[0];
  if (!targetSocket) {
    st.cancelConnection();
    return true;
  }
  st.completeConnection(nodeId, targetSocket.id);
  ui.consumeOneShotConnect();
  if (useUIStore.getState().canvasConnectMode === 'sticky') {
    startFromNode(nodeId);
  }
  return true;
}
